import axios from 'axios'
import toast from 'react-hot-toast'

// ── Global rate-limit pause state ─────────────────────────────────────────────
// When the server returns 429, all background polls check this flag and skip
// their call until the server's retry window expires.
let _pausedUntil = 0   // epoch ms; 0 = not paused

export const isApiPaused    = () => Date.now() < _pausedUntil
export const getApiPauseMs  = () => Math.max(0, _pausedUntil - Date.now())

/** Pause all background polling for `seconds` seconds. */
export const pauseApiPolls = (seconds = 60) => {
  _pausedUntil = Date.now() + seconds * 1000
  console.warn(`[api] Background polls paused for ${seconds}s (until ${new Date(_pausedUntil).toLocaleTimeString()})`)
}

// Create axios instance
const api = axios.create({
  baseURL: '/api',
  timeout: 60000,
})

// Request interceptor — attach auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token')
    if (token) config.headers.Authorization = `Bearer ${token}`
    return config
  },
  (error) => Promise.reject(error)
)

// Response interceptor — handle 401 and 429
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status

    if (status === 401) {
      localStorage.removeItem('token')
      window.location.href = '/login'
    }

    if (status === 429) {
      // Read the server's retryAfter or fall back to 60 s
      const retryAfter = error.response?.data?.retryAfter ?? 60
      pauseApiPolls(retryAfter)
      // Use the plain toast (not .error) so it shows as a visible banner,
      // not suppressed into the error log
      toast(
        `⏸ Background refresh paused ${retryAfter}s — too many requests. Data shown is from last fetch.`,
        { id: 'rate-limit-pause', duration: retryAfter * 1000, icon: '⚠️' }
      )
    }

    return Promise.reject(error)
  }
)

// Auth API
export const authAPI = {
  register: (email, password) => api.post('/auth/register', { email, password }),
  login: (email, password) => api.post('/auth/login', { email, password }),
  logout: () => api.post('/auth/logout'),
  getProfile: () => api.get('/auth/me'),
  getKiteLoginUrl: () => api.get('/auth/kite/login-url'),
  kiteCallback: (requestToken) => api.post('/auth/kite/callback', { request_token: requestToken }),
  verifyPassword: (password) => api.post('/auth/verify-password', { password }),
}

// Portfolio API
export const portfolioAPI = {
  getHoldings: () => api.get('/portfolio/holdings'),
  getPositions: () => api.get('/portfolio/positions'),
  getOrders: () => api.get('/portfolio/orders'),
  getTrades: () => api.get('/portfolio/trades'),
  getMargins: () => api.get('/portfolio/margins'),
  getDailyPnL: (startDate, endDate) => api.get('/portfolio/pnl/daily', { 
    params: { startDate, endDate } 
  }),
  placeOrder: (orderData) => api.post('/portfolio/orders', orderData),
  cancelOrder: (orderId) => api.delete(`/portfolio/orders/${orderId}`),
}

// Watchlist API
export const watchlistAPI = {
  getWatchlists: () => api.get('/watchlist'),
  createWatchlist: (name) => api.post('/watchlist', { name }),
  createNifty500Watchlist: () => api.post('/watchlist/create-nifty500'),
  addInstrument: (watchlistId, instrument) => api.post(`/watchlist/${watchlistId}/instruments`, instrument),
  removeInstrument: (watchlistId, instrumentId) => api.delete(`/watchlist/${watchlistId}/instruments/${instrumentId}`),
  getQuotes: (watchlistId) => api.get(`/watchlist/${watchlistId}/quotes`),
  deleteWatchlist: (watchlistId) => api.delete(`/watchlist/${watchlistId}`),
}

// Signals API
export const signalsAPI = {
  getSignals: (params) => api.get('/signals', { params }),
  generateSignals: (data) => api.post('/signals/generate', data),
  executeSignal: (signalId) => api.post(`/signals/${signalId}/execute`),
  cancelOrder: (signalId) => api.post(`/signals/${signalId}/cancel-order`),
  getSignalChart: (signalId) => api.get(`/signals/${signalId}/chart`),
  cancelSignal: (signalId) => api.delete(`/signals/${signalId}`),
  getStats: () => api.get('/signals/stats'),
}

// Reports API
export const reportsAPI = {
  getPerformance: (params) => api.get('/reports/performance', { params }),
  getInstruments: (params) => api.get('/reports/instruments', { params }),
  getStrategies: (params) => api.get('/reports/strategies', { params }),
  getMonthlySummary: () => api.get('/reports/monthly-summary'),
}

// Settings API
export const settingsAPI = {
  getSettings: () => api.get('/settings'),
  updateKiteApiKey: (apiKey) => api.put('/settings/kite-api-key', { apiKey }),
  toggleAgentMode: (enabled) => api.put('/settings/agent-mode', { enabled }),
  updateNotifications: (notifications) => api.put('/settings/notifications', notifications),
  updateTradingSettings: (settings) => api.put('/settings/trading', settings),
  updateTelegram: (chatId) => api.put('/settings/telegram', { chatId }),
  getLogs: () => api.get('/settings/logs'),
  // AngelOne streaming credentials
  getAngelOneSettings: () => api.get('/settings/angelone'),
  updateAngelOneSettings: (creds) => api.put('/settings/angelone', creds),
  updateStreamingProvider: (provider) => api.put('/settings/streaming-provider', { provider }),
}

// Streaming API (AngelOne)
export const streamingAPI = {
  start:          () => api.post('/streaming/start'),
  stop:           () => api.post('/streaming/stop'),
  getStatus:      () => api.get('/streaming/status'),
  subscribe:      (symbols) => api.post('/streaming/subscribe', { symbols }),
}

// Backtest API
export const backtestAPI = {
  runBacktest: (params) => api.post('/backtest/run', params),
}

// Prompt API
export const promptAPI = {
  sendPrompt: (prompt) => api.post('/prompt/send', { prompt }),
  getHistory: () => api.get('/prompt/history'),
}

// Recommendations API
export const recommendationsAPI = {
  getIntradayRecommendations: () => api.get('/recommendations/intraday'),
  getGeminiFinalRecommendation: (payload) => api.post('/recommendations/gemini-final', payload),
  saveStreamingMinute: (payload) => api.post('/recommendations/streaming/minute', payload),
  saveStreamingMinuteV2: (payload) => api.post('/recommendations/streaming/minute-v2', payload),
  getRecentStreaming: ({ minutes }) => api.get('/recommendations/streaming/recent', { params: { minutes } }),
  getLatestStreaming: (timeWindow) => api.get('/recommendations/streaming/latest', { 
    params: timeWindow 
  }),
}

// Tick Data API
export const tickDataAPI = {
  saveBulkTickData: (tickData) => api.post('/tickdata/bulk-save', { tickData }),
  getTickDataBySymbol: (symbol, params) => api.get(`/tickdata/symbol/${symbol}`, { params }),
  getLatestTickData: () => api.get('/tickdata/latest'),
  getTickDataStats: (period) => api.get('/tickdata/stats', { params: { period } }),
  cleanupOldData: (daysOld) => api.delete('/tickdata/cleanup', { params: { daysOld } }),
}

// Stock Data API
export const stockDataAPI = {
  getOHLC: (stockname) => axios.get(`https://serendpt.in/get-ohlc?stockname=${stockname}`),
  getBulkOHLC: (stocknames) => axios.post('https://serendpt.in/get-bulk-ohlc', { stocknames }),
}

// Margin Calculator API
export const marginAPI = {
  getOrderMargin: (order) => api.post('/margins/orders', [order]),
};

export const kiteAPI = {
  getQuote: (symbol) => api.get(`/quote/${symbol}`),
  getBulkQuote: (symbols) => api.get(`/quote/bulk/${symbols}`),
};

// SL Orders API
export const slOrdersAPI = {
  checkAndPlace: () => api.post('/slorders/check-and-place'),
};

// Trades API
export const tradesAPI = {
  getTodayTrades: () => api.get('/trades/today'),
  getTradeHistory: (params) => api.get('/trades/history', { params }),
  getTradesBySymbol: (symbol, params) => api.get(`/trades/symbol/${symbol}`, { params }),
  getClosedPositions: (params) => api.get('/trades/closed-positions', { params }),
  uploadCsv: (csvContent) => api.post('/trades/upload-csv', { csvContent }),
  getHoldingAges: () => api.get('/trades/holding-ages'),
};

// Automation API
export const automationAPI = {
  getSettings: () => api.get('/automation/settings'),
  updateSettings: (settings) => api.put('/automation/settings', settings),
  runCycle: () => api.post('/automation/run-cycle'),
  getLogs: (params) => api.get('/automation/logs', { params }),
  clearLogs: () => api.delete('/automation/logs'),
  getOrders: (params) => api.get('/automation/orders', { params }),
  forceSell: (symbol, orderData) => api.post(`/automation/force-sell/${symbol}`, orderData),
  exportLogs: (params) => api.get('/automation/export-logs', { params }),
};
// Mutual Funds API
export const mfAPI = {
  getTransactions: () => api.get('/mf/transactions'),
  addTransaction: (tx) => api.post('/mf/transactions', tx),
  bulkImport: (transactions, replace = false) => api.post('/mf/transactions/bulk', { transactions, replace }),
  updateTransaction: (id, tx) => api.put(`/mf/transactions/${id}`, tx),
  deleteTransaction: (id) => api.delete(`/mf/transactions/${id}`),
  getNav: (isins) => api.get(`/mf/nav?isins=${isins.join(',')}`),
};

// External Holdings API
export const externalHoldingsAPI = {
  get: (accountName) => api.get(`/external-holdings/${accountName}`),
  upload: (accountName, holdings) => api.post(`/external-holdings/${accountName}`, { holdings }),
  uploadNormalized: (accountName, holdings) => api.post(`/external-holdings/${accountName}`, { holdings, normalized: true }),
  clear: (accountName) => api.delete(`/external-holdings/${accountName}`),
}

// Portfolio Snapshot API
export const portfolioSnapshotAPI = {
  save: (accounts) => api.post('/portfolio-snapshots', { accounts }),
  getAll: () => api.get('/portfolio-snapshots'),
}

// Portfolio Health API
export const healthAPI = {
  getSummary:   () => api.get('/health/summary'),
  refresh:      () => api.post('/health/refresh'),
  getHoldings:  () => api.get('/health/holdings'),
  getNews:      () => api.get('/health/news'),
  refreshNews:  () => api.post('/health/news/refresh'),
  getPulse:     () => api.get('/health/pulse'),
  // Full 5-factor ranked analysis with reasoning + sentiment + source links
  getAnalysis:  (forceRefresh = false) => api.get(`/health/analysis${forceRefresh ? '?refresh=1' : ''}`),
}

// Rebalance API — /api/rebalance/*
export const rebalanceAPI = {
  getStatus:         ()         => api.get('/rebalance/status'),
  getHoldingsScored: ()         => api.get('/rebalance/holdings-scored'),
  getCandidates:     (limit=20) => api.get(`/rebalance/candidates?limit=${limit}`),
  getUniverse:       (limit=100)=> api.get(`/rebalance/universe?limit=${limit}`),
  getScore:          (symbol)   => api.get(`/rebalance/score/${encodeURIComponent(symbol)}`),
  scoreHoldings:     ()         => api.post('/rebalance/score-holdings'),
  scoreUniverse:     ()         => api.post('/rebalance/score-universe'),
  generatePlan:      ()         => api.post('/rebalance/generate'),
  // Rebalance v2 — risk-aware optimizer plan. Optional policy overrides in `overrides`
  // (e.g. { optimizerMode:'erc', noTradeBandPct:0.04 }).
  generatePlanV2:    (overrides={}) => api.post('/rebalance/generate-v2', overrides),
  // Covariance/correlation snapshot for held (+ optional extra) symbols.
  getRiskModel:      ({ symbols, days, shrinkage, full } = {}) => {
    const q = new URLSearchParams()
    if (symbols?.length) q.set('symbols', Array.isArray(symbols) ? symbols.join(',') : symbols)
    if (days)      q.set('days', days)
    if (shrinkage != null) q.set('shrinkage', shrinkage)
    if (full)      q.set('full', '1')
    const qs = q.toString()
    return api.get(`/rebalance/risk-model${qs ? `?${qs}` : ''}`)
  },
  getPlans:          ()         => api.get('/rebalance/plans'),
  getPlan:           (id)       => api.get(`/rebalance/plans/${id}`),
  updatePlanStatus:  (id, s)    => api.put(`/rebalance/plans/${id}/status`, { status: s }),
  // Explainability + history
  explain:           (symbol)   => api.get(`/rebalance/explain/${encodeURIComponent(symbol)}`),
  getHistory:        (symbol, days=90) => api.get(`/rebalance/history/${encodeURIComponent(symbol)}?days=${days}`),
  getHistoryAll:     (days=60)  => api.get(`/rebalance/history-all?days=${days}`),
  // Backtesting
  runBacktest:       (body)     => api.post('/rebalance/backtest', body),
  getBacktests:      ()         => api.get('/rebalance/backtests'),
  getBacktest:       (id)       => api.get(`/rebalance/backtests/${id}`),
  // Portfolio diagnostics — sector breakdown, concentration, score profile (read-only)
  getDiagnostics:    ()         => api.get('/rebalance/diagnostics'),
  // Prediction tracking
  resolveForwardReturns: (windowDays=30) => api.post('/rebalance/resolve-forward-returns', { windowDays }),
  getScoreVsReturn:  (window=30) => api.get(`/rebalance/score-vs-return?window=${window}`),
}

// Paper Trading API — /api/paper/*
// Architecture note: routes live at /api/paper/* (not under /api/rebalance/)
// so they can be re-exposed under a future dedicated Trading section without
// any backend changes.
export const paperAPI = {
  getStatus:      ()          => api.get('/paper/status'),
  getConfig:      ()          => api.get('/paper/config'),
  updateConfig:   (cfg)       => api.put('/paper/config', cfg),
  setKillSwitch:  (engage, reason) => api.post('/paper/config/kill-switch', { engage, reason }),
  runCycle:       ()          => api.post('/paper/cycle'),
  runMtm:         ()          => api.post('/paper/mtm'),
  getPositions:   ()          => api.get('/paper/positions'),
  getTrades:      (params)    => api.get('/paper/trades', { params }),
  getTrade:       (id)        => api.get(`/paper/trades/${id}`),
  getCycles:      (params)    => api.get('/paper/cycles', { params }),
  getCycle:       (id)        => api.get(`/paper/cycles/${id}`),
  getPerformance: ()          => api.get('/paper/performance'),
  getRejected:    (params)    => api.get('/paper/rejected', { params }),
  // Phase 3 research routes
  getResearch:         ()          => api.get('/paper/research'),
  getRejectedAnalysis: (params)    => api.get('/paper/research/rejected', { params }),
}

// Alerts API
export const alertsAPI = {
  getRules:    () => api.get('/alerts/rules'),
  createRule:  (r) => api.post('/alerts/rules', r),
  updateRule:  (id, r) => api.put(`/alerts/rules/${id}`, r),
  deleteRule:  (id) => api.delete(`/alerts/rules/${id}`),
  getEvents:   (status) => api.get('/alerts/events', { params: status ? { status } : {} }),
  updateEvent: (id, body) => api.put(`/alerts/events/${id}`, body),
  evaluate:     () => api.post('/alerts/evaluate'),
  seedDefaults: (force = false) => api.post('/alerts/seed-defaults', { force }),
}

// MF Intelligence API — /api/mf-intel/*
export const mfIntelAPI = {
  // Phase 1 — Universe
  syncUniverse:    ()           => api.post('/mf-intel/universe/sync'),
  syncOwned:       ()           => api.post('/mf-intel/universe/sync-owned'),
  syncScheme:      (code)       => api.post(`/mf-intel/universe/sync-scheme/${code}`),
  universeStats:   ()           => api.get('/mf-intel/universe/stats'),
  searchSchemes:   (params)     => api.get('/mf-intel/universe/search', { params }),
  getScheme:       (code)       => api.get(`/mf-intel/universe/${code}`),

  // Phase 2 — Holdings
  getHoldings:     ()           => api.get('/mf-intel/holdings'),
  getHoldingsSummary: ()        => api.get('/mf-intel/holdings/summary'),
  getHolding:      (key)        => api.get(`/mf-intel/holdings/${encodeURIComponent(key)}`),

  // Phase 3 — Category Intelligence
  holdingRankings: ()           => api.get('/mf-intel/holdings/rankings'),
  leaderboard:     (cat, p)     => api.get(`/mf-intel/category/${encodeURIComponent(cat)}/leaderboard`, { params: p }),
  categoryStats:   (cat, p)     => api.get(`/mf-intel/category/${encodeURIComponent(cat)}/stats`, { params: p }),
  subCategories:   (cat)        => api.get(`/mf-intel/category/${encodeURIComponent(cat)}/subcategories`),

  // Phase 4 — Scoring
  computeScore:    (code)       => api.post(`/mf-intel/scores/compute/${code}`),
  computeCategory: (cat, p)     => api.post(`/mf-intel/scores/compute-category/${encodeURIComponent(cat)}`, {}, { params: p }),
  getScore:        (code)       => api.get(`/mf-intel/scores/${code}`),
  topScored:       (cat, p)     => api.get(`/mf-intel/scores/top/${encodeURIComponent(cat)}`, { params: p }),
  holdingScores:   ()           => api.get('/mf-intel/scores/holdings'),

  // Phase 5 — Multi-Horizon Ranking
  computeRanks:    (cat, p)     => api.post(`/mf-intel/ranks/compute/${encodeURIComponent(cat)}`, {}, { params: p }),
  holdingRanks:    ()           => api.get('/mf-intel/ranks/holdings'),
  consistentLeaders: (cat, p)   => api.get(`/mf-intel/ranks/consistent/${encodeURIComponent(cat)}`, { params: p }),
  rankMatrix:      (cat, p)     => api.get(`/mf-intel/ranks/matrix/${encodeURIComponent(cat)}`, { params: p }),
  getSchemeRank:   (code)       => api.get(`/mf-intel/ranks/${code}`),

  // Phase 6 — Persistence
  buildPersistence: (cat, p)    => api.post(`/mf-intel/persist/build/${encodeURIComponent(cat)}`, {}, { params: p }),
  persistLeaders:  (cat, p)     => api.get(`/mf-intel/persist/leaders/${encodeURIComponent(cat)}`, { params: p }),
  persistProfile:  (code)       => api.get(`/mf-intel/persist/${code}`),

  // Phase 7 — Opportunity
  categoryOpportunities: (cat, p) => api.get(`/mf-intel/opportunity/${encodeURIComponent(cat)}`, { params: p }),
  holdingOpportunities:  ()       => api.get('/mf-intel/opportunity/holdings'),
  schemeOpportunity:     (code)   => api.get(`/mf-intel/opportunity/scheme/${code}`),

  // Phase 8 — Benchmark
  benchmark:       (code)       => api.get(`/mf-intel/benchmark/${code}`),
  alphaRanking:    (cat, p)     => api.get(`/mf-intel/benchmark/alpha/${encodeURIComponent(cat)}`, { params: p }),

  // Phase 9 — Health Engine
  holdingsHealth:     ()        => api.get('/mf-intel/health/holdings'),
  healthSummary:      (cat, p)  => api.get(`/mf-intel/health/summary/${encodeURIComponent(cat)}`, { params: p }),
  getFundHealth:      (code)    => api.get(`/mf-intel/health/${code}`),
  computeHealth:      (code, body) => api.post(`/mf-intel/health/compute/${code}`, body || {}),
  computeHealthCat:   (cat, p)  => api.post(`/mf-intel/health/compute-category/${encodeURIComponent(cat)}`, {}, { params: p }),

  // Phase 10 — Replacement Engine
  generatePlan:    ()  => api.post('/mf-intel/replace/generate'),
  getReplacePlan:  ()  => api.get('/mf-intel/replace/plan'),

  // Phase 11 — True Exposure Engine
  getExposure:       ()                    => api.get('/mf-intel/exposure'),
  exposureStatus:    ()                    => api.get('/mf-intel/exposure/status'),
  importPortfolio:   (code, body)          => api.post(`/mf-intel/exposure/import/${code}`, body),
  deletePortfolio:   (code, month)         => api.delete(`/mf-intel/exposure/portfolio/${code}/${month}`),

  // Phase 12 — Overlap Intelligence
  getOverlap:        ()                    => api.get('/mf-intel/overlap'),

  // Phase 13 — Portfolio Fragility Engine
  getFragility:      ()                    => api.get('/mf-intel/fragility'),

  // Phase 14 — Allocation Engine
  getAllocation:     (p)                   => api.get('/mf-intel/allocation', { params: p }),
  getAllocationTemplate: (p)               => api.get('/mf-intel/allocation/template', { params: p }),

  // Phase 15 — Market Regime Engine
  getRegime:         ()                    => api.get('/mf-intel/regime'),
  getRegimeHistory:  (days)               => api.get('/mf-intel/regime/history', { params: { days } }),

  // Phase 16 — Portfolio Simulator
  simulate:          (moves)              => api.post('/mf-intel/simulate', { moves }),
  simulateMove:      (body)               => api.post('/mf-intel/simulate/move', body),

  // Phase 17 — Research Lab
  researchSummary:      ()       => api.get('/mf-intel/research/summary'),
  researchAMC:          (p)      => api.get('/mf-intel/research/amc', { params: p }),
  researchCategories:   ()       => api.get('/mf-intel/research/categories'),
  researchExpense:      ()       => api.get('/mf-intel/research/expense'),
  researchResilience:   (p)      => api.get('/mf-intel/research/resilience', { params: p }),
  researchBeaters:      (p)      => api.get('/mf-intel/research/beaters', { params: p }),
  researchDistribution: ()       => api.get('/mf-intel/research/distribution'),
  researchPersistence:  ()       => api.get('/mf-intel/research/persistence'),

  // Universe Rankings — top funds for comparison and fund picking
  getUniverseTop:       (p)      => api.get('/mf-intel/universe/top', { params: p }),
  universeCheck:        ()       => api.get('/mf-intel/universe/check'),
  getCuratedCodes:        (p)    => api.get('/mf-intel/universe/curated-codes', { params: p }),
  scoreUniverseBatch:     (data) => api.post('/mf-intel/universe/score-batch', data, { timeout: 120000 }),
  purgeNonInvestable:     ()     => api.post('/mf-intel/universe/purge-non-investable'),

  // Investment Plan — unified fund-specific action plan
  getInvestmentPlan:    (p)      => api.get('/mf-intel/investment-plan', { params: p }),

  // Phase 18 — Annual Rotation Engine
  generateRotation:     ()       => api.post('/mf-intel/rotation/generate'),
  getRotationPlan:      ()       => api.get('/mf-intel/rotation/plan'),

  // Phase 19 — Monthly Investment Committee
  generateCommittee:    ()       => api.post('/mf-intel/committee/generate'),
  getCommitteeLatest:   (p)      => api.get('/mf-intel/committee/latest', { params: p }),
  getCommitteeHistory:  ()       => api.get('/mf-intel/committee/history'),

  // ── Investable Universe Audit (Phase A) ───────────────────────────────────
  auditUniverseReport:   ()       => api.get('/mf-intel/audit/universe-report', { timeout: 60000 }),
  auditStampInvestable:  ()       => api.post('/mf-intel/audit/stamp-investable', {}, { timeout: 300000 }),
  auditPurgeScores:      ()       => api.post('/mf-intel/audit/purge-scores', {}, { timeout: 120000 }),
  // Data quality audit for a specific fund
  auditValidateFund:     (name)   => api.get(`/mf-intel/audit/validate/${encodeURIComponent(name)}`, { timeout: 30000 }),
  auditConfidence:       (code)   => api.get(`/mf-intel/audit/confidence/${code}`),
  // Category universe counts and rankings (Phase B)
  auditCategoryCounts:     ()       => api.get('/mf-intel/audit/category-counts', { timeout: 60000 }),
  auditCategoryRankings:   (params) => api.get('/mf-intel/audit/category-rankings', { params, timeout: 60000 }),
  // Phase B — category dashboard (medians + ranked lists per category)
  auditCategoryDashboard:  (params) => api.get('/mf-intel/audit/category-dashboard', { params, timeout: 90000 }),

  // Phase C — explainability API
  explainScheme:         (code)  => api.get(`/mf-intel/explain/${code}`, { timeout: 30000 }),
  explainValidationSet:  ()      => api.get('/mf-intel/explain/validation-set', { timeout: 120000 }),

  // Phase F — portfolio validation (HOLD/WATCH/REDUCE/REPLACE per holding)
  portfolioValidate:     ()      => api.get('/mf-intel/portfolio/validate', { timeout: 120000 }),

  // Phase G — replacement candidate audit (top peers beating current holdings)
  replacementAudit:      ()      => api.get('/mf-intel/portfolio/replacement-audit', { timeout: 180000 }),
}

// ── Knowledge Workspace (file-based persistence, /api/workspace) ─────────────
export const workspaceAPI = {
  listNotes:   ()                 => api.get('/workspace/notes'),
  getNote:     (id)               => api.get(`/workspace/notes/${id}`),
  createNote:  (body = {})        => api.post('/workspace/notes', body),
  updateNote:  (id, body)         => api.put(`/workspace/notes/${id}`, body),
  deleteNote:  (id)               => api.delete(`/workspace/notes/${id}`),       // soft delete → trash
  listTrash:   ()                 => api.get('/workspace/trash'),
  restoreNote: (id)               => api.post(`/workspace/trash/${id}/restore`),
  destroyNote: (id)               => api.delete(`/workspace/trash/${id}`),       // permanent — trash only
  search:      (q)                => api.get('/workspace/search', { params: { q } }),

  // Attachments — raw bytes up, blob down (auth header travels with axios)
  uploadAttachment: (noteId, file) =>
    api.post(
      `/workspace/notes/${noteId}/attachments?name=${encodeURIComponent(file.name)}&mime=${encodeURIComponent(file.type || 'application/octet-stream')}`,
      file,
      { headers: { 'Content-Type': 'application/octet-stream' }, timeout: 120000 }
    ),
  fetchAttachmentBlob: (noteId, attId) =>
    api.get(`/workspace/notes/${noteId}/attachments/${attId}`, { responseType: 'blob', timeout: 120000 }),
}

// ── Public contact form (/api/contact) — no auth ────────────────────────────
export const contactAPI = {
  send: (payload) => api.post('/contact', payload),
}

export default api